import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import KoraxFormHistoryDialog from "../KoraxFormHistoryDialog";

const styles = {
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
    marginBottom: 9
  },
  input: {
    width: "100%",
    border: "1px solid #DCE5F1",
    borderRadius: 9,
    padding: 12,
    background: "#FFFFFF",
    color: "#17243B",
    fontSize: 13,
    fontFamily: "inherit"
  }
};

const readError = error => {
  const value =
    error?.response?.data?.error ||
    error?.response?.data?.message;

  return typeof value === "string"
    ? value
    : error?.message ||
      "Não foi possível concluir a solicitação.";
};

const resolveLink = path => {
  if (
    typeof path !== "string" ||
    !(
      /^\/forms\/f\/[a-f0-9-]{36}\/[a-f0-9]{64}$/i.test(path) ||
      /^\/f\/[A-Za-z0-9_-]{24}$/.test(path)
    )
  ) {
    throw new Error("O servidor retornou um link inválido.");
  }

  return new URL(path, window.location.origin).href;
};

export default function KoraxFormSendDialog({
  open,
  onClose,
  ticketId
}) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [message, setMessage] = useState(
    "Olá! Para continuarmos seu atendimento, " +
    "por favor preencha o formulário abaixo:"
  );
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;

    setForms([]);
    setSelectedId("");
    setPending(null);
    setError("");
    setLoading(true);

    api.get("/korax-forms")
      .then(response => {
        if (!active) return;

        const list = Array.isArray(response.data?.forms)
          ? response.data.forms
          : [];

        setForms(
          list.filter(item => item.status === "active")
        );
      })
      .catch(err => {
        if (active) setError(readError(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, ticketId]);

  const selected = forms.find(
    form => String(form.id) === String(selectedId)
  );

  const close = () => {
    if (!sending) onClose();
  };

  const send = async () => {
    if (!selected || !ticketId || sending) return;

    setSending(true);
    setError("");

    let messageAttempted = false;

    try {
      let current = pending;

      if (!current) {
        const response = await api.post(
          `/korax-forms/${selected.id}/requests`,
          { ticketId: Number(ticketId) }
        );

        current = {
          formId: selected.id,
          requestId: response.data.id,
          url: resolveLink(response.data.urlPath)
        };

        setPending(current);
      }

      if (String(current.formId) !== String(selected.id)) {
        throw new Error(
          "O formulário selecionado mudou. Abra novamente."
        );
      }

      const body = [
        message.trim(),
        `*${selected.title}*`,
        current.url
      ].filter(Boolean).join("\n\n");

      messageAttempted = true;

      await api.post(`/messages/${ticketId}`, {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body,
        isPrivate: "false"
      });

      toast.success(
        "Solicitação de envio registrada no atendimento."
      );

      onClose();
    } catch (err) {
      setError(
        messageAttempted
          ? "Não foi possível confirmar o envio. " +
            "Confira a conversa antes de tentar novamente " +
            "para evitar mensagens duplicadas. " +
            readError(err)
          : readError(err)
      );
    } finally {
      setSending(false);
    }
  };

  if (historyOpen) {
    return (
      <KoraxFormHistoryDialog
        open={open}
        ticketId={ticketId}
        onBack={() => setHistoryOpen(false)}
        onClose={() => {
          setHistoryOpen(false);
          onClose();
        }}
      />
    );
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={close}
      fullWidth
      maxWidth="sm"
      disableBackdropClick={sending}
      disableEscapeKeyDown={sending}
      PaperProps={{
        style: { borderRadius: 15 }
      }}
    >
      <DialogTitle>
        <div style={{
          fontWeight: 800,
          color: "#14243D",
          fontSize: 19
        }}>
          Enviar formulário
        </div>
        <div style={{
          marginTop: 5,
          fontSize: 12,
          color: "#718096"
        }}>
          Escolha um modelo para esta conversa.
        </div>
      </DialogTitle>

      <DialogContent dividers>
        <label style={styles.label}>
          Formulário
        </label>

        {loading ? (
          <div style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            padding: 15,
            color: "#718096"
          }}>
            <CircularProgress size={17} />
            Carregando modelos...
          </div>
        ) : (
          <select
            aria-label="Selecionar formulário"
            style={styles.input}
            value={selectedId}
            disabled={sending || Boolean(pending)}
            onChange={e => {
              setSelectedId(e.target.value);
              setError("");
            }}
          >
            <option value="">
              Selecione um formulário...
            </option>

            {forms.map(form => (
              <option key={form.id} value={form.id}>
                {form.title}
              </option>
            ))}
          </select>
        )}

        {!loading && forms.length === 0 && !error && (
          <p style={{
            color: "#718096",
            fontSize: 12,
            lineHeight: 1.6
          }}>
            Nenhum formulário ativo encontrado.
            Crie um modelo na área Formulários e ative-o.
          </p>
        )}

        {selected && (
          <p style={{
            color: "#718096",
            fontSize: 12,
            marginTop: 10
          }}>
            {selected.fields?.length || 0} perguntas
            {" · "}Link exclusivo válido por 7 dias
          </p>
        )}

        <div style={{ marginTop: 22 }}>
          <label style={styles.label}>
            Mensagem de acompanhamento
          </label>

          <textarea
            style={{
              ...styles.input,
              resize: "vertical",
              lineHeight: 1.6
            }}
            rows={3}
            maxLength={350}
            value={message}
            disabled={sending}
            onChange={e => setMessage(e.target.value)}
          />
        </div>

        {pending && (
          <div style={{
            background: "#EFF5FF",
            color: "#2854A3",
            borderRadius: 9,
            padding: 13,
            marginTop: 17,
            fontSize: 12,
            lineHeight: 1.65
          }}>
            <strong>Link individual gerado.</strong>
            <div style={{
              overflowWrap: "anywhere",
              marginTop: 7
            }}>
              {pending.url}
            </div>
            <p style={{ marginBottom: 0 }}>
              Se o envio falhou, verifique a conversa antes
              de tentar novamente. Enquanto esta janela
              permanecer aberta, o mesmo link será reutilizado.
            </p>
          </div>
        )}

        {error && (
          <div role="alert" style={{
            background: "#FFF1F2",
            color: "#A33347",
            borderRadius: 9,
            padding: 12,
            marginTop: 16,
            fontSize: 12,
            lineHeight: 1.6
          }}>
            {error}
          </div>
        )}

        <p style={{
          color: "#8794A7",
          fontSize: 11,
          lineHeight: 1.6,
          marginTop: 18
        }}>
          O cliente receberá o link pelo WhatsApp desta
          conversa e poderá preenchê-lo pelo celular.
        </p>
      </DialogContent>

      <DialogActions style={{ padding: "14px 20px" }}>
        <Button
          onClick={() => setHistoryOpen(true)}
          disabled={sending}
          style={{
            textTransform: "none",
            color: "#0057FF",
            fontWeight: 700,
            marginRight: "auto"
          }}
        >
          Ver histórico
        </Button>
        <Button
          onClick={close}
          disabled={sending}
          style={{ textTransform: "none" }}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={send}
          disabled={loading || sending || !selected}
          style={{
            background: "#0057FF",
            color: "#FFFFFF",
            borderRadius: 9,
            fontWeight: 700,
            textTransform: "none",
            opacity: loading || sending || !selected
              ? .6 : 1
          }}
        >
          {sending
            ? "Enviando..."
            : "Enviar pelo WhatsApp"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
